import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard,
  Calculator,
  FileText,
  Database,
  CircleDollarSign,
  Users,
  LogOut,
  HardHat,
  Layers,
  Settings,
  Grid3x3,
  Pencil,
  Menu,
  X,
} from "lucide-react";

const navMain = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
];

const navRacks = [
  { to: "/configurator", label: "Configurator", icon: Calculator, testid: "nav-configurator" },
  { to: "/quotations", label: "Quotations", icon: FileText, testid: "nav-quotations" },
];

const navMezzanine = [
  { to: "/mezzanine", label: "Configurator", icon: Calculator, testid: "nav-mezzanine" },
  { to: "/mezzanine-quotations", label: "Quotations", icon: FileText, testid: "nav-mezzanine-quotes" },
];

const navSlotted = [
  { to: "/slotted", label: "Configurator", icon: Calculator, testid: "nav-slotted" },
  { to: "/slotted-quotations", label: "Quotations", icon: FileText, testid: "nav-slotted-quotes" },
];

const navFloorPlan = [
  { to: "/floorplan", label: "Layouts", icon: Pencil, testid: "nav-floorplan" },
  { to: "/floorplan-quotations", label: "Quotations", icon: FileText, testid: "nav-floorplan-quotes" },
];

const navAdmin = [
  { to: "/admin/master-data", label: "Rack Master Data", icon: Database, testid: "nav-master" },
  { to: "/admin/pricing", label: "Rack Pricing", icon: CircleDollarSign, testid: "nav-pricing" },
  { to: "/admin/mezzanine-settings", label: "Mezzanine Settings", icon: Settings, testid: "nav-mz-settings" },
  { to: "/admin/slotted-settings", label: "Slotted Angle Settings", icon: Settings, testid: "nav-sa-settings" },
  { to: "/admin/rack-library", label: "Rack Library", icon: Settings, testid: "nav-rack-library" },
  { to: "/admin/users", label: "Users", icon: Users, testid: "nav-users" },
];

function Item({ to, label, icon: Icon, testid, onClick }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-5 py-3 text-sm font-medium border-l-[3px] transition-colors ${
          isActive
            ? "nav-active"
            : "border-transparent text-ink-muted hover:text-ink hover:bg-surfaceAlt"
        }`
      }
    >
      <Icon size={18} strokeWidth={1.75} />
      <span className="tracking-wide">{label}</span>
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <div className="min-h-screen lg:flex bg-canvas app-shell" data-testid="app-layout">
      {/* Mobile top bar (visible <lg) */}
      <header
        className="lg:hidden sticky top-0 z-30 bg-white border-b border-line h-14 flex items-center justify-between px-4 no-print"
        data-testid="mobile-topbar"
      >
        <button
          onClick={() => setOpen(true)}
          className="p-2 -ml-2 text-ink"
          aria-label="Open navigation"
          data-testid="mobile-menu-btn"
        >
          <Menu size={22} />
        </button>
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => navigate("/dashboard")}
        >
          <img
            src="/brand/adarsh-logo.png"
            alt="Adarsh Udyog"
            className="h-8 w-auto object-contain"
          />
        </div>
        <div className="w-8" /> {/* spacer to balance the menu button */}
      </header>

      {/* Mobile sidebar backdrop */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40 no-print"
          onClick={close}
          data-testid="mobile-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          bg-white border-r border-line flex flex-col shrink-0 no-print
          fixed lg:static inset-y-0 left-0 z-50 w-72 lg:w-64 overflow-y-auto
          transition-transform duration-200
          ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
        `}
        data-testid="sidebar"
      >
        <div
          className="h-20 px-5 flex items-center justify-between gap-3 border-b border-line"
        >
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => { navigate("/dashboard"); close(); }}
            data-testid="sidebar-logo"
          >
            <img
              src="/brand/adarsh-logo.png"
              alt="Adarsh Udyog"
              className="h-12 w-auto object-contain"
            />
          </div>
          <button
            onClick={close}
            className="lg:hidden p-2 -mr-2 text-ink-muted"
            aria-label="Close navigation"
            data-testid="mobile-close-btn"
          >
            <X size={20} />
          </button>
        </div>

        <div className="py-4">
          <div className="label-tech px-5 mb-2">Workspace</div>
          {navMain.map((n) => <Item key={n.to} {...n} onClick={close} />)}

          <div className="label-tech px-5 mt-5 mb-2 flex items-center gap-2">
            <HardHat size={12} className="text-brand" /> Pallet Racks
          </div>
          {navRacks.map((n) => <Item key={n.to} {...n} onClick={close} />)}

          <div className="label-tech px-5 mt-5 mb-2 flex items-center gap-2">
            <Layers size={12} className="text-brand" /> Mezzanine
          </div>
          {navMezzanine.map((n) => <Item key={n.to} {...n} onClick={close} />)}

          <div className="label-tech px-5 mt-5 mb-2 flex items-center gap-2">
            <Grid3x3 size={12} className="text-brand" /> Slotted Angle
          </div>
          {navSlotted.map((n) => <Item key={n.to} {...n} onClick={close} />)}

          {user?.role === "admin" && (
            <>
              <div className="label-tech px-5 mt-5 mb-2 flex items-center gap-2">
                <Pencil size={12} className="text-brand" /> Floor Plan
              </div>
              {navFloorPlan.map((n) => <Item key={n.to} {...n} onClick={close} />)}
            </>
          )}

          {user?.role === "admin" && (
            <>
              <div className="label-tech px-5 mt-5 mb-2">Administration</div>
              {navAdmin.map((n) => (
                <Item key={n.to} {...n} onClick={close} />
              ))}
            </>
          )}
        </div>

        <div className="mt-auto border-t border-line">
          <div className="px-5 py-4">
            <div className="label-tech">{user?.role}</div>
            <div className="text-sm font-semibold text-ink mt-1 truncate" data-testid="sidebar-user-name">
              {user?.name}
            </div>
            <div className="text-xs text-ink-muted truncate">{user?.email}</div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-5 py-3 text-sm font-bold uppercase tracking-wider border-t border-line text-ink-muted hover:bg-surfaceAlt hover:text-brand-danger"
          >
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-x-hidden">{children}</main>
    </div>
  );
}

export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-6 lg:pt-8 pb-5 lg:pb-6 border-b border-line bg-white" data-testid="page-header">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="min-w-0">
          {eyebrow && <div className="label-tech mb-2">{eyebrow}</div>}
          <h1 className="display font-black text-ink text-2xl sm:text-3xl lg:text-4xl tracking-tighter uppercase break-words">
            {title}
          </h1>
          {subtitle && <p className="text-ink-muted mt-2 max-w-2xl text-sm">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2 sm:gap-3">{actions}</div>}
      </div>
    </div>
  );
}
